// 🔒 LAYOUT — LOCKED 2026-05-03 by Giovanni Barcelos / MidiaHub
// Footer v2: sem nav, sem "Marina Serra Canastra", textos em negrito
// Logo = SVG inline (LogoSVG / LogoHorizontal) — fundo 100% transparente
// Header bg = Footer bg = skin bg — transição sem delay
// Active section tracked via IntersectionObserver — menu highlight segue scroll
// DO NOT replace SVG logos with PNG images without Giovanni's order

import { useState, useEffect, useRef } from 'react';
import { useSkin } from '@/components/SkinContext';
import { SkinPicker } from '@/components/SkinPicker';
import { LogoSVG } from '@/components/LogoSVG';
import { NAV_ITEMS } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { activeSkin } = useSkin();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(80);

  const bg  = activeSkin.bg;
  const ink = activeSkin.ink;

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Header height tracking
  useEffect(() => {
    const updateH = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    updateH();
    const ro = new ResizeObserver(updateH);
    if (headerRef.current) ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  // 🔒 Active section via IntersectionObserver — tracks which section is visible
  useEffect(() => {
    const sectionIds = ['home', ...NAV_ITEMS.map(i => i.href.replace('#', '')), 'contato'];
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { rootMargin: `-${headerHeight + 20}px 0px -55% 0px`, threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach(o => o.disconnect());
  }, [headerHeight]);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const headerH = headerRef.current?.offsetHeight ?? 80;
      const top = el.getBoundingClientRect().top + window.scrollY - headerH;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  // Nav link style with active state
  const navStyle = (href: string): React.CSSProperties => {
    const id = href.replace('#', '');
    const isActive = activeSection === id;
    return {
      color: ink,
      textDecoration: 'none',
      fontSize: '0.85rem',
      fontWeight: isActive ? 700 : 500,
      opacity: isActive ? 1 : 0.65,
      letterSpacing: '0.02em',
      transition: 'opacity 0.2s, font-weight 0.2s',
      cursor: 'pointer',
      borderBottom: isActive ? `2px solid ${ink}` : '2px solid transparent',
      paddingBottom: '2px',
    };
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bg }}>

      {/* ── HEADER ── */}
      <header ref={headerRef} style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 100,
        backgroundColor: bg,
        boxShadow: scrolled ? `0 2px 20px ${ink}22` : 'none',
        transition: 'box-shadow 0.3s ease, background-color 0.35s ease',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          padding: '0 1.5rem', height: 80,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>

          {/* 🔒 Logo header — SVG inline, fundo transparente, cor = ink */}
          <a href="#home" onClick={(e) => handleNav(e, '#home')}
            style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <LogoSVG color={ink} width={68} iconOnly={true} />
          </a>

          {/* Desktop nav */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: '1.75rem' }}
               className="hidden lg:flex">
            {NAV_ITEMS.map(item => (
              <a key={item.href} href={item.href}
                onClick={(e) => handleNav(e, item.href)}
                style={navStyle(item.href)}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => {
                  const id = item.href.replace('#', '');
                  e.currentTarget.style.opacity = activeSection === id ? '1' : '0.65';
                }}>
                {item.label}
              </a>
            ))}
            <a href="#contato" onClick={(e) => handleNav(e, '#contato')} style={{
              backgroundColor: ink, color: bg,
              padding: '0.45rem 1.2rem', borderRadius: '2px',
              fontWeight: 700, fontSize: '0.85rem',
              textDecoration: 'none', letterSpacing: '0.04em',
              transition: 'opacity 0.2s', cursor: 'pointer',
              opacity: activeSection === 'contato' ? 1 : 0.9,
            }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
              onMouseLeave={e => (e.currentTarget.style.opacity = activeSection === 'contato' ? '1' : '0.9')}>
              Contato
            </a>
          </nav>

          {/* Right controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <SkinPicker />
            <button className="flex lg:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              aria-label="Menu">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{
                    width: 22, height: 2,
                    backgroundColor: ink,
                    display: 'block', borderRadius: 1,
                  }} />
                ))}
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div style={{
            backgroundColor: bg,
            borderTop: `2px solid ${ink}44`,
            padding: '1.5rem 1.5rem 2rem',
            display: 'flex', flexDirection: 'column', gap: '0',
          }}>
            {NAV_ITEMS.map(item => (
              <a key={item.href} href={item.href}
                onClick={(e) => handleNav(e, item.href)}
                style={{
                  color: ink,
                  textDecoration: 'none',
                  fontWeight: activeSection === item.href.replace('#', '') ? 900 : 600,
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  opacity: 1,
                  padding: '0.85rem 0',
                  borderBottom: `1px solid ${ink}18`,
                  display: 'block',
                  letterSpacing: '0.02em',
                }}>
                {item.label}
              </a>
            ))}
            <a href="#contato" onClick={(e) => handleNav(e, '#contato')} style={{
              backgroundColor: ink, color: bg,
              padding: '0.85rem 1.5rem', borderRadius: '2px',
              fontWeight: 700, fontSize: '1rem',
              textDecoration: 'none', textAlign: 'center', cursor: 'pointer',
              marginTop: '1.25rem', display: 'block',
              letterSpacing: '0.04em',
            }}>
              Contato
            </a>
          </div>
        )}
      </header>

      {/* Main content */}
      <main style={{ paddingTop: headerHeight }}>{children}</main>

      {/* ── FOOTER ── */}
      {/* 🔒 Footer bg = skin bg / Logo = SVG inline, fundo transparente */}
      <footer style={{
        backgroundColor: bg,
        padding: '3rem 2rem 2rem',
        borderTop: `1px solid ${ink}22`,
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: '1.5rem',
        }}>

          {/* 🔒 Logo footer — SVG inline, fundo transparente, cor = ink */}
          <LogoSVG color={ink} width={180} iconOnly={false} />

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
            <p style={{ color: ink, opacity: 0.7, fontSize: '0.82rem', textAlign: 'center', margin: 0, fontWeight: 700 }}>
              © 2026 Mídias com Propósito — Comunicação Estratégica
            </p>
            {/* 🔒 Crédito MidiaHub — link clicável para midiahub.com */}
            <p style={{ color: ink, opacity: 0.6, fontSize: '0.78rem', margin: '0.35rem 0 0', textAlign: 'center', fontWeight: 700 }}>
              Feito com Propósito para Marina Perillo por{' '}
              <a
                href="https://midiahub.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: ink,
                  opacity: 1,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  fontWeight: 700,
                }}
              >
                Giovanni Barcelos | midiahub.com
              </a>
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
}
